import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import pick from 'lodash.pick';
import mixpanel from 'mixpanel-browser';
import { combineLatest, filter, Subject, takeUntil } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { Navigation, SwiperOptions } from 'swiper';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AuthService } from '../../firebase/auth.service';
import { ItemStatus } from '../../shared/models/item.models';
import { IToolbarData } from '../../shared/models/components-data.models';
import { EventsStore } from '../events-store';
import { EventItem, successEventModalData, EVENT_TYPE_ENUMS } from '../events';
import { PodStore } from 'src/app/merfolk/pods/pod-store';
import { environment } from '../../../environments/environment';
import { UserService } from 'src/app/auth/user.service';
import { ReactionsService } from '../../services/reactions.service';
import { IonTabs } from '@ionic/angular';
import { PopoverController, ToastController } from '@ionic/angular';
import { ShareOptionsComponent } from '../../activity-feed/post-details/share-options/share-options.component';
import { AngularFirestore } from '@angular/fire/compat/firestore';

const TOOLBAR_DATA: IToolbarData = {
  arrowRouter: '/events',
  btnSlot: 'end',
  title: 'Event',
};

const TOOLBAR_SWIPER: IToolbarData = {
  arrow: true,
  btnSlot: 'end',
  title: 'Events',
};

@Component({
  selector: 'app-events-details',
  templateUrl: './events-details.component.html',
  styleUrls: ['./events-details.component.scss'],
})
export class EventsDetailsComponent implements OnInit, OnDestroy {
  @ViewChild('col') col: ElementRef;
  @ViewChild(IonTabs, { static: false }) tabs: IonTabs;
  @ViewChild('swiper') swiper: ElementRef;

  TOOLBAR_DATA = TOOLBAR_DATA;
  TOOLBAR_SWIPER = TOOLBAR_SWIPER;
  config: SwiperOptions;

  eventItem;
  podItem;
  galleryUrls: string[];
  currentUser;
  currentUserID;
  creator;
  eventCreator;
  status: ItemStatus;
  isEventFree: boolean = false;
  isEventPaid: boolean = false;
  event: EventItem;
  isOpen = false;
  showSuccessModal = false;
  successModalData;
  isOpenFormModal = false;
  isEventJoined: boolean = false;
  isCanJoin: boolean = false;
  infoSnapshot;
  userInfo;
  showStatusModal: boolean = false;
  itemStatus;
  properties;
  isAdmin;
  flagData;
  isContentFlaggedModal;
  isOpenFlagModal;
  isFlagging = false;
  selectedTab: string = 'about'; // Default to 'about' tab
  isTabLoading: boolean = false; // Add loading state for tabs
  isCommentsLoaded: boolean = false; // Track if comments are already loaded

  discordServerInviteLink = environment.discordServerInviteLink;

  contactLink: string;

  likesInfo;
  loadingLikeButton = false;
  itemId;
  likesCount: number = 0;
  commentsCount: number = 0;
  hasLiked: boolean = false;

  private destroy$ = new Subject<void>();

  constructor(
    private eventStore: EventsStore,
    private activatedRoute: ActivatedRoute,
    private authService: AuthService,
    private podStore: PodStore,
    private userService: UserService,
    private reactionsService: ReactionsService,
    private router: Router,
    private popoverCtrl: PopoverController,
    private toastCtrl: ToastController,
    private fireStore: AngularFirestore
  ) {
    this.itemId = this.activatedRoute.snapshot.params.id;
  }

  ngOnInit() {
    mixpanel.init(environment.mixpanelToken, { debug: true });
    const { uid } = this.authService.isUserAuth();
    mixpanel.identify(uid);
    this.getUser();
    this.eventStore.getItemByByValue({
      documentId: this.activatedRoute.snapshot.params.id,
    });

    // Check if there's a tab query parameter
    const tabParam = this.activatedRoute.snapshot.queryParamMap.get('tab');
    if (tabParam) {
      this.selectedTab = tabParam;
    }

    this.listenSecondHandDetails();
    this.eventStore.selectShowFormModal().subscribe(isOpen => {
      this.isOpenFormModal = isOpen;
    });
    this.eventStore.selectShowPendingModal().subscribe(isOpen => {
      this.showSuccessModal = isOpen;
    });
    this.successModalData = successEventModalData;

    // Get likes and comments count
    this.loadReactions();
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.eventItem = null;
    this.podItem = null;
    this.isCanJoin = false;
    this.isEventJoined = false;
    if (this.infoSnapshot) {
      this.infoSnapshot.unsubscribe();
    }
  }

  trackEvent(event, eventValue, creator) {
    this.properties = {
      type: eventValue?.eventType,
      organizer: [creator?.username, eventValue?.creator],
    };

    mixpanel.track(event, this.properties);
  }

  toggleGallery(index?: number) {
    this.isOpen = !this.isOpen;
    if (this.isOpen && index !== undefined) {
      this.config = {
        initialSlide: index,
        slidesPerView: 1,
        spaceBetween: 0,
        pagination: true,
        navigation: true,
      };

      // Allow time for the modal to open before initializing swiper
      setTimeout(() => {
        if (this.swiper) {
          this.swiper.nativeElement.initialize();
          this.swiper.nativeElement.swiper.slideTo(index);
        }
      }, 300);
    }
  }

  openModal() {
    this.eventStore.updateShowFormModal(true);
  }

  joinEvent() {
    const newEvent = Object.assign({
      ...this.eventItem,
      participants: this.eventItem?.participants?.length
        ? [
            ...this.eventItem?.participants,
            pick(this.currentUser, ['id', 'profilePicture', 'username']),
          ]
        : [pick(this.currentUser, ['id', 'profilePicture', 'username'])],
    });

    this.eventStore.updateProfile(newEvent);
    this.trackEvent('Joined Event', newEvent, this.creator);
  }

  async flagContent(slide) {
    if (this.isFlagging) return;

    this.isFlagging = true;
    slide.type = 'events';
    this.flagData = slide;

    try {
      const functions = getFunctions();
      const isContentFlagged = httpsCallable(functions, 'isContentFlagged');
      const res = await isContentFlagged({
        itemId: slide.id,
        url: slide.images[0],
        type: this.flagData.type,
      });

      if (res.data === true) {
        this.isContentFlaggedModal = true;
      } else {
        this.isOpenFlagModal = true;
      }
    } catch (error) {
      console.error('Error checking flag status:', error);
    } finally {
      this.isFlagging = false;
    }
  }

  closeModal(e) {
    e ? this.eventStore.updateShowFormModal(false) : null;
  }

  handleOk(e) {
    if (e) {
      this.eventStore.updateShowPendingModal(false);
      this.showSuccessModal = false;
      // Navigate back to events list
      this.router.navigate(['/events']);
    }
  }

  handleCancel(e) {
    if (e) {
      this.eventStore.updateShowPendingModal(false);
      this.showSuccessModal = false;
      // Navigate back to events list
      this.router.navigate(['/events']);
    }
  }

  openStatusModal(status?) {
    this.showStatusModal = !this.showStatusModal;
    if (status) this.itemStatus = status;
  }

  //Not Used
  /*
  navigateToMeetingLink() {
    window.location.href = this.eventItem?.meetingLink;
  }
  */
  private listenSecondHandDetails() {
    this.eventStore
      .selectItem()
      .pipe(
        filter(item => !!item),
        takeUntil(this.destroy$)
      )
      .subscribe(item => {
        this.eventItem = item;
        this.galleryUrls = item.images;
        this.status = item.status as ItemStatus;
        this.isEventFree = item.price?.value === '0';
        this.isEventPaid = item.price?.value !== '0';
        this.event = item;

        if (item.podName) {
          this.listenPodDetails(item.podName);
        }

        this.isEventJoined = this.eventItem.participants?.some(
          element => element.id === this.currentUser?.uid
        );

        if (item.podName) {
          this.podStore.getItems({});
          this.listenPodDetails(item.podName);
        } else {
          this.isCanJoin =
            this.eventItem?.participants?.find(
              element => element.id !== this.currentUser?.uid
            ) && !this.isEventJoined;
        }

        this.authService.getUserByUid(item.creator.id).subscribe(user => {
          this.creator = user;
          this.eventCreator = user;
          if (this.eventItem?.externalEventLink) {
            this.contactLink = this.eventItem.externalEventLink.startsWith(
              'http'
            )
              ? this.eventItem.externalEventLink
              : `https://${this.eventItem.externalEventLink}`;
          } else {
            this.contactLink = this.creator?.discordLink;
          }
        });
      });
  }

  private listenPodDetails(id) {
    combineLatest([this.podStore.selectItems()])
      .pipe(
        takeUntil(this.destroy$),
        map(([items]) => items && items.find(item => item.id === id))
      )
      .subscribe(item => {
        this.podItem = item;
        if (this.eventItem.onlyPodMembers) {
          this.isCanJoin =
            item?.participants.includes(this.currentUser?.uid) &&
            !this.isEventJoined;
        } else {
          this.isCanJoin = !this.eventItem.participants.some(
            participant => participant.id === this.currentUser?.uid
          );
        }
      });
  }

  private getUser() {
    this.userService.currentUser$
      .pipe(
        takeUntil(this.destroy$),
        filter(user => !!user.uid)
      )
      .subscribe(user => {
        this.currentUser = user;
        this.currentUserID = user.uid;
        this.isAdmin = this.userService.isAdmin;
        this.userInfo = this.userService.balanceInfo;
        mixpanel.identify(user.uid);
      });
  }

  isExternallyHosted(event) {
    return (
      event.eventType === EVENT_TYPE_ENUMS.EXTERNALLY_HOSTED_AND_PAID ||
      event.eventType === EVENT_TYPE_ENUMS.EXTERNALLY_HOSTED_AND_FREE
    );
  }

  /**
   * Limits a description to the first 5 words
   * @param description The full description text
   * @returns The first 5 words of the description followed by ellipsis if needed
   */
  limitDescriptionToFiveWords(description: string): string {
    if (!description) return '';

    const words = description.trim().split(/\s+/);
    if (words.length <= 15) return description;

    return words.slice(0, 5).join(' ') + '...';
  }

  /**
   * Formats the date in the "Fri, Mar 15" format always,
   * because time is displayed separately
   * @param date The date to format
   * @returns Formatted date string
   */
  formatDateForDisplay(date: any): string {
    if (!date) return '';

    try {
      let startDateObj: Date;
      let endDateObj: Date;

      // Handle Firebase Timestamp objects for start date
      if (this.eventItem?.startDate?.seconds) {
        startDateObj = new Date(this.eventItem.startDate.seconds * 1000);
      }
      // Handle Firebase Timestamp objects with _seconds for start date
      else if (this.eventItem?.startDate?._seconds) {
        startDateObj = new Date(this.eventItem.startDate._seconds * 1000);
      }
      // Handle ISO string date (YYYY-MM-DD format) for start date
      else if (
        typeof this.eventItem?.startDate === 'string' &&
        this.eventItem?.startDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        startDateObj = new Date(this.eventItem.startDate);
      }
      // Handle regular date strings or objects for start date
      else if (
        typeof this.eventItem?.startDate === 'string' ||
        this.eventItem?.startDate instanceof Date
      ) {
        startDateObj = new Date(this.eventItem.startDate);
      } else {
        return '';
      }

      // Handle Firebase Timestamp objects for end date
      if (this.eventItem?.endDate?.seconds) {
        endDateObj = new Date(this.eventItem.endDate.seconds * 1000);
      }
      // Handle Firebase Timestamp objects with _seconds for end date
      else if (this.eventItem?.endDate?._seconds) {
        endDateObj = new Date(this.eventItem.endDate._seconds * 1000);
      }
      // Handle ISO string date (YYYY-MM-DD format) for end date
      else if (
        typeof this.eventItem?.endDate === 'string' &&
        this.eventItem?.endDate.match(/^\d{4}-\d{2}-\d{2}$/)
      ) {
        endDateObj = new Date(this.eventItem.endDate);
      }
      // Handle regular date strings or objects for end date
      else if (
        typeof this.eventItem?.endDate === 'string' ||
        this.eventItem?.endDate instanceof Date
      ) {
        endDateObj = new Date(this.eventItem.endDate);
      }

      const options: Intl.DateTimeFormatOptions = {
        month: 'long',
        day: 'numeric',
      };

      const startFormatted = startDateObj
        .toLocaleDateString('en-US', options)
        .toUpperCase();

      // If no end date or same date, just show start date
      if (
        !endDateObj ||
        startDateObj.toDateString() === endDateObj.toDateString()
      ) {
        // For detail page, include time if showTimeFields is true
        if (this.router.url.includes('/events/')) {
          if (this.eventItem?.showTimeFields) {
            const startTime = this.formatTimeForDisplay(
              this.eventItem?.startTime
            );
            const endTime = this.formatTimeForDisplay(this.eventItem?.endTime);
            return `${startFormatted}, ${startTime}${
              endTime ? ` - ${endTime}` : ''
            }`;
          } else {
            return startFormatted;
          }
        } else {
          // For overview page, just show date
          return startFormatted;
        }
      }

      // Format for date range
      const endFormatted = endDateObj
        .toLocaleDateString('en-US', options)
        .toUpperCase();

      // For detail page, include time if showTimeFields is true
      if (this.router.url.includes('/events/')) {
        if (this.eventItem?.showTimeFields) {
          const startTime = this.formatTimeForDisplay(
            this.eventItem?.startTime
          );
          const endTime = this.formatTimeForDisplay(this.eventItem?.endTime);
          return `${startFormatted}, ${startTime} - ${endFormatted}, ${endTime}`;
        } else {
          return `${startFormatted} - ${endFormatted}`;
        }
      } else {
        // For overview page, just show date range
        return `${startFormatted} - ${endFormatted}`;
      }
    } catch (e) {
      console.warn('Error formatting date for display:', e);
      return '';
    }
  }

  /**
   * Formats a time value for display
   * @param time The time to format
   * @returns Formatted time string (12-hour format)
   */
  formatTimeForDisplay(time: any): string {
    if (!time) return '';

    try {
      let timeObj: Date;

      // Handle different time formats
      if (time.seconds) {
        // Firebase Timestamp
        timeObj = new Date(time.seconds * 1000);
      } else if (time._seconds) {
        // Firebase Timestamp with _seconds
        timeObj = new Date(time._seconds * 1000);
      } else if (typeof time === 'number') {
        // Timestamp in milliseconds
        timeObj = new Date(time);
      } else if (typeof time === 'string') {
        // ISO string or other string format
        timeObj = new Date(time);
      } else if (time instanceof Date) {
        // Date object
        timeObj = time;
      } else {
        return '';
      }

      // Format time in 12-hour format without minutes if they're 00
      const hours = timeObj.getHours();
      const minutes = timeObj.getMinutes();
      const ampm = hours >= 12 ? 'pm' : 'am';
      const hour12 = hours % 12 || 12;

      return minutes === 0
        ? `${hour12}${ampm}`
        : `${hour12}:${minutes.toString().padStart(2, '0')}${ampm}`;
    } catch (e) {
      console.warn('Error formatting time for display:', e);
      return '';
    }
  }

  // Add method to handle tab changes with loading effect
  onTabChange(event: any) {
    const newTab = event.detail.value;

    if (this.selectedTab === newTab) {
      return; // Skip if same tab is selected
    }

    // Simulate loading state but keep header visible
    this.isTabLoading = true;

    // Maintain scroll position at the top to keep header visible
    const cardElement = document.getElementById('cardDetail');
    if (cardElement) {
      cardElement.scrollTop = 0;
    }

    // If switching to comments and they haven't been loaded yet
    if (newTab === 'comments' && !this.isCommentsLoaded) {
      setTimeout(() => {
        this.selectedTab = newTab;
        this.isTabLoading = false;
        this.isCommentsLoaded = true;
      }, 300); // Short timeout for a smooth effect
    } else {
      // For already loaded tabs or About tab
      setTimeout(() => {
        this.selectedTab = newTab;
        this.isTabLoading = false;
      }, 150); // Shorter timeout for Already loaded content
    }

    // Preload comments tab data if we're on about tab
    // This ensures comments are ready when user switches tabs
    if (this.selectedTab === 'about' && !this.isCommentsLoaded) {
      // Set a timer to preload comments data after a short delay
      setTimeout(() => {
        this.isCommentsLoaded = true;
      }, 1000); // Delay preloading to prevent performance issues
    }
  }

  async shareEvent(event: any) {
    const postUrl = `${window.location.origin}/events/${this.eventItem.id}`;
    const postTitle = this.eventItem?.title || 'MerMapp Event';
    const postText =
      this.eventItem?.description || 'Check out this event on MerMapp!';

    const popover = await this.popoverCtrl.create({
      component: ShareOptionsComponent,
      componentProps: {
        postUrl: postUrl,
        postTitle: postTitle,
        postText: postText,
        onOptionSelected: (option: string) => {
          this.handleShareOption(option, postUrl, postTitle, postText);
          popover.dismiss();
        },
      },
      event: event,
      translucent: true,
      backdropDismiss: true,
      alignment: 'center',
      side: 'top',
      arrow: true,
    });

    await popover.present();
  }

  async handleShareOption(
    option: string,
    postUrl: string,
    postTitle: string,
    postText: string
  ) {
    switch (option) {
      case 'copy':
        await this.copyToClipboard(postUrl);
        break;
      case 'facebook':
        window.open(
          `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
            postUrl
          )}`,
          '_blank'
        );
        break;
      case 'twitter':
        window.open(
          `https://twitter.com/intent/tweet?url=${encodeURIComponent(
            postUrl
          )}&text=${encodeURIComponent(postText)}`,
          '_blank'
        );
        break;
      case 'whatsapp':
        window.open(
          `https://api.whatsapp.com/send?text=${encodeURIComponent(
            `${postText} ${postUrl}`
          )}`,
          '_blank'
        );
        break;
      case 'email':
        window.open(
          `mailto:?subject=${encodeURIComponent(
            postTitle
          )}&body=${encodeURIComponent(`${postText} ${postUrl}`)}`,
          '_blank'
        );
        break;
      case 'native':
        await this.useNativeShare(postUrl, postTitle, postText);
        break;
    }
  }

  async copyToClipboard(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      const toast = await this.toastCtrl.create({
        message: 'Link copied to clipboard',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  }

  async useNativeShare(postUrl: string, title: string, text: string) {
    if (navigator.share) {
      try {
        await navigator.share({
          title: title,
          text: text,
          url: postUrl,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error sharing:', error);
        }
      }
    } else {
      const toast = await this.toastCtrl.create({
        message: 'Share functionality not available on this device',
        duration: 2000,
        position: 'bottom',
      });
      await toast.present();
    }
  }

  openChat() {
    if (this.creator?.id) {
      this.router.navigate(['/chat/detail', this.creator.id]);
    }
  }

  // Load reactions data from the available reactions service
  loadReactions() {
    if (!this.itemId) return;

    // Get all reactions
    this.reactionsService
      .getReactions()
      .pipe(take(1))
      .subscribe((reactions: any[]) => {
        const itemReaction = reactions.find(r => r.id === this.itemId);
        if (itemReaction) {
          this.likesCount = itemReaction.likes?.length || 0;
          this.hasLiked =
            itemReaction.likes?.includes(this.currentUser?.uid) || false;
        } else {
          this.likesCount = 0;
          this.hasLiked = false;
        }
      });

    // Get comments count from Firestore
    this.fireStore
      .collection('comments', ref => ref.where('eventId', '==', this.itemId))
      .get()
      .pipe(take(1))
      .subscribe(snapshot => {
        this.commentsCount = snapshot.size;
      });
  }

  // Use the available putLikeOrDislike function
  putLikeOrDislike() {
    if (this.loadingLikeButton) return;
    this.loadingLikeButton = true;

    // Toggle like status optimistically for better UX
    this.hasLiked = !this.hasLiked;
    this.likesCount = this.hasLiked
      ? this.likesCount + 1
      : Math.max(0, this.likesCount - 1);

    // Call the actual service method
    this.reactionsService.putLikeOrDislike(this.itemId, 'events');

    // Reset loading state after a short delay since putLikeOrDislike doesn't return an observable
    setTimeout(() => {
      this.loadingLikeButton = false;
    }, 500);
  }
}

